import { isSessionGraceActive } from "~/auth/sessionGrace";
import { showNotification } from "~/hooks/useNotificationModal";
import { errorReporter } from "./errorReporter";
import {
  AWSAuthError,
  AWSError,
  ClockSkewError,
  InternalError,
  LambdaError,
  NetworkError,
  S3Error,
  UserError,
} from "./exceptions";
import { toUserFacingError } from "./userFacingError";

// AWS SDK signals clock skew via these error names. Lambda/STS use
// InvalidSignatureException; S3 uses RequestTimeTooSkewed. aws-sdk v2
// sets `code` on the error object; v3 sets `name`. Check both.
export const isClockSkewError = (e: unknown): boolean => {
  if (!(e instanceof Error)) return false;
  const code = (e as { code?: string }).code;
  const matches = (s: string | undefined) =>
    s === "InvalidSignatureException" || s === "RequestTimeTooSkewed";
  return matches(e.name) || matches(code);
};

// Module-level guard so a clock-skew window doesn't open a modal per
// in-flight request. Reset on full page reload (i.e. once the user has
// presumably fixed their clock).
let clockSkewModalShown = false;

export type ServiceResult<T> = {
  data: T | null;
  error: Error | null;
};

export type ErrorPredicate = (error: Error) => boolean;

// Generic function wrapper with error handling
export function withErrorHandling<Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
  errorPredicates: ErrorPredicate[] = [],
) {
  return async (...args: Args): Promise<ServiceResult<R>> => {
    try {
      const result = await fn(...args);
      return { data: result, error: null };
    } catch (error) {
      const errorObj = error as Error;

      for (const predicate of errorPredicates) {
        if (predicate(errorObj)) {
          if (globalErrorHandler(errorObj)) {
            return { data: null, error: errorObj };
          } else {
            throw errorObj;
          }
        }
      }
      throw errorObj;
    }
  };
}

export const showError = (
  title: string,
  message: string,
  devDetails?: string[],
) => {
  showNotification(title, message, undefined, undefined, devDetails);
};

export const handleTokenExpiration = () => {
  // While the user is in expired-session grace mode, the SessionExpiredBanner
  // already covers this. Suppress the reload modal — reloading would blow away
  // the unsaved work the grace mode exists to preserve.
  if (isSessionGraceActive()) {
    return;
  }
  showNotification(
    "Session Expired",
    "Your session has expired. Please refresh the page.",
    () => {
      window.location.reload();
    },
  );
};

/**
 * The error classes this module owns a notification for. Anything else is left
 * unhandled so `withErrorHandling` rethrows it to the caller, which is the
 * pre-existing contract.
 */
const isNotifiableError = (error: Error): boolean =>
  error instanceof LambdaError ||
  error instanceof S3Error ||
  error instanceof UserError ||
  error instanceof NetworkError ||
  error instanceof InternalError;

/**
 * Displays a UI notification corresponding to a given application error.
 * This function is responsible for the user-facing part of error handling.
 * @param error The error object.
 * @returns `true` if a notification was shown, `false` otherwise.
 */
export const showGlobalErrorNotification = (error: Error): boolean => {
  // Clock skew can surface either as a ClockSkewError directly or wrapped
  // inside a Lambda/S3 error (originalError carries the raw AWS SDK error).
  if (
    error instanceof ClockSkewError ||
    (error instanceof AWSError && isClockSkewError(error.originalError))
  ) {
    if (!clockSkewModalShown) {
      clockSkewModalShown = true;
      showNotification(
        "Your computer clock is out of sync",
        "Indigo can't reach the server because your computer's date/time " +
          "doesn't match. Please enable automatic time-sync in your system " +
          "settings, then click below to refresh.",
        () => {
          window.location.reload();
        },
      );
    }
    return true;
  }
  if (error instanceof AWSAuthError) {
    handleTokenExpiration();
    return true;
  }

  // Everything below is a service failure we own the copy for. The error's own
  // `message` is technical (physical Lambda name, status code, handler body)
  // and only belongs in Sentry — `toUserFacingError` produces what the user
  // reads, plus dev-environment-only technical lines.
  if (!isNotifiableError(error)) {
    return false; // Error was not handled
  }

  const { title, message, devDetails } = toUserFacingError(error);
  showError(title, message, devDetails);
  return true;
};

// Global error handler
export const globalErrorHandler = (error: Error): boolean => {
  // Report the error to Sentry with application-specific context
  errorReporter.reportApplicationError(error, {
    tags: {
      handler: "globalErrorHandler",
    },
  });

  return showGlobalErrorNotification(error);
};
